/**
 * Un chiffre mesuré, ou l'aveu qu'on ne le sait pas (V1.6-11).
 *
 * **C'est le composant qui tient tout le ticket.** Un écran qui écrit
 * `value ?? 0` transforme « on n'a jamais rien demandé à ce fournisseur » en
 * « ce fournisseur ne répond jamais », et personne ne s'en aperçoit avant
 * d'avoir écarté quelqu'un pour rien.
 *
 * Trois états, deux affichages :
 *
 *     AVAILABLE          le chiffre, avec son échantillon
 *     INSUFFICIENT_DATA  « pas assez de données »
 *     NOT_AVAILABLE      « non mesurable »
 *
 * Les deux derniers se ressemblent à l'écran mais ne demandent pas la même
 * chose : l'un s'améliore en travaillant, l'autre demanderait un changement de
 * modèle. L'infobulle le dit.
 */
import type { Metric } from "@/api/types";

import {
  formatMetric,
  NOT_ENOUGH,
  NOT_MEASURABLE,
  type MetricKind,
} from "./metric-display";

export function MetricValue({
  metric,
  kind,
  className,
}: {
  metric: Metric;
  kind: MetricKind;
  className?: string;
}) {
  if (metric.status !== "AVAILABLE" || metric.value === null) {
    const why = metric.status === "NOT_AVAILABLE" ? NOT_MEASURABLE : NOT_ENOUGH;
    return (
      <span className={`text-ink-subtle ${className ?? ""}`} title={why}>
        —
      </span>
    );
  }
  return (
    <span className={`tnum text-ink ${className ?? ""}`}>{formatMetric(metric, kind)}</span>
  );
}

/**
 * Une métrique nommée, avec son échantillon et sa définition.
 *
 * La définition n'est pas décorative : quelqu'un qui voit « 40 % » doit pouvoir
 * comprendre pourquoi, sans lire le code.
 */
export function MetricCard({
  label,
  metric,
  kind,
  explanation,
  sampleLabel,
}: {
  label: string;
  metric: Metric;
  kind: MetricKind;
  explanation: string;
  sampleLabel?: string;
}) {
  const known = metric.status === "AVAILABLE" && metric.value !== null;
  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface p-3">
      <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</p>
      <p className="mt-1 text-xl font-semibold leading-none">
        <MetricValue metric={metric} kind={kind} />
      </p>
      <p className="mt-1 text-[11px] text-ink-subtle">
        {known
          ? `sur ${metric.sample_size} ${sampleLabel ?? "observations"}`
          : metric.status === "NOT_AVAILABLE"
            ? "non mesurable"
            : "pas assez de données"}
      </p>
      <p className="mt-2 text-[11px] leading-snug text-ink-muted">{explanation}</p>
    </div>
  );
}
